











import {Component, Prop, Vue, Watch} from 'vue-property-decorator';

// Use legacy build: `build/pdf.js` uses private fields (`#`) that Webpack 4 cannot parse without Babel;
// Babel-transpiling pdfjs broke CJS export getters. Legacy is ES5-compatible and pairs with legacy worker.
// Types come from src/shims-pdfjs.d.ts (TS 3.x cannot lex upstream `#private` fields in .d.ts files).
// `import type` (TS 3.8+) is unavailable; use `import()` in type position (TS 2.9+) instead —
// ts-loader strips it before Babel runs so Babel never sees the type-only import.
// tslint:disable-next-line:no-var-requires
const pdfjsLib = require('pdfjs-dist/legacy/build/pdf.js') as typeof import('pdfjs-dist/legacy/build/pdf.js');

const baseUrl = process.env.BASE_URL || '/';
pdfjsLib.GlobalWorkerOptions.workerSrc = baseUrl.replace(/\/?$/, '/') + 'pdf.worker.min.js';

type PdfLoadingTask = import('pdfjs-dist/legacy/build/pdf.js').PDFLoadingTask;
type PdfDocument = import('pdfjs-dist/legacy/build/pdf.js').PDFDocumentProxy;

@Component
export default class PDF extends Vue {
    @Prop() readonly src!: string;

    loading = true;
    error: string | null = null;

    private loadGeneration = 0;
    private activeTask: PdfLoadingTask | null = null;
    private activeDoc: PdfDocument | null = null;

    mounted(): void {
        this.loadDocument();
    }

    beforeDestroy(): void {
        this.teardownDocument();
    }

    @Watch('src')
    onSrcChange(): void {
        this.loadDocument();
    }

    private teardownDocument(): void {
        if (this.activeTask) {
            this.activeTask.destroy();
            this.activeTask = null;
        }
        if (this.activeDoc) {
            this.activeDoc.destroy();
            this.activeDoc = null;
        }
        const scroll = this.$refs.scroll as HTMLElement | undefined;
        if (scroll) {
            scroll.innerHTML = '';
        }
    }

    private async measureContainerWidth(): Promise<number> {
        // Measure the outer container (always in the DOM) because the inner scroll div
        // has display:none while loading=true, which makes clientWidth 0.
        const el = this.$el as HTMLElement | undefined;
        let w = el ? el.clientWidth : 0;
        for (let i = 0; i < 6 && w < 8; i++) {
            await this.$nextTick();
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            w = el ? el.clientWidth : 0;
        }
        return Math.max(w, 1);
    }

    private async loadDocument(): Promise<void> {
        const gen = ++this.loadGeneration;

        this.teardownDocument();

        if (!this.src) {
            this.loading = false;
            this.error = 'Ingen PDF-adress.';
            return;
        }

        this.loading = true;
        this.error = null;

        const task = pdfjsLib.getDocument({url: this.src, withCredentials: true});
        this.activeTask = task;

        try {
            const pdf = await task.promise;
            if (gen !== this.loadGeneration) {
                pdf.destroy();
                return;
            }
            this.activeTask = null;
            this.activeDoc = pdf;

            await this.$nextTick();
            const scroll = this.$refs.scroll as HTMLElement | undefined;
            if (!scroll) {
                if (gen === this.loadGeneration) {
                    this.activeDoc = null;
                }
                pdf.destroy();
                return;
            }

            const containerWidth = await this.measureContainerWidth() * 0.97;

            for (let i = 1; i <= pdf.numPages; i++) {
                if (gen !== this.loadGeneration) {
                    return;
                }
                const page = await pdf.getPage(i);
                if (gen !== this.loadGeneration) {
                    return;
                }
                const baseViewport = page.getViewport({scale: 1});
                const scale = containerWidth / baseViewport.width;
                const viewport = page.getViewport({scale});

                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                if (!context) {
                    continue;
                }
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                scroll.appendChild(canvas);
                const renderTask = page.render({canvasContext: context, viewport});
                await renderTask.promise;
            }
        } catch {
            if (gen === this.loadGeneration) {
                this.error = 'Kunde inte visa PDF:en.';
            }
        } finally {
            if (gen === this.loadGeneration) {
                this.loading = false;
                if (this.activeTask === task) {
                    this.activeTask = null;
                }
            }
        }
    }
}
